import React, { type FunctionComponent, useMemo } from 'react'
import _groupBy from 'lodash/groupBy'
import styled from '@emotion/styled'
import { spacing } from '@instacart/ids-core'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'
import {
  OptionType,
  type RetailerLocationStoreGroup,
  type GroupedOptions,
} from 'domains/account-management/components/retailer-location-store-group-select/utils/types'
import { ValuePill } from 'domains/account-management/components/retailer-location-store-group-select/components/ValuePill'

const ValuesContainer = styled.div({
  marginTop: spacing.s24,
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  columnGap: spacing.s8,
  rowGap: spacing.s16,
})

interface ValueDisplayProps {
  value: RetailerLocationStoreGroup
  options: GroupedOptions[]
  onRemove: (type: OptionType, value: string) => void
}

export const ValueDisplay: FunctionComponent<ValueDisplayProps> = ({
  value,
  options,
  onRemove,
}) => {
  const groupedValues = useMemo(() => _groupBy(value, 'type'), [value])
  const selectedStoreGroups = groupedValues[OptionType.StoreGroup] ?? []
  const selectedRetailerLocations = groupedValues[OptionType.RetailerLocation] ?? []
  const hasAllRetailerLocations = useMemo(
    () =>
      selectedRetailerLocations.length > 0 &&
      selectedRetailerLocations.length ===
        options?.filter(option => option.type === OptionType.RetailerLocation)[0]?.options?.length,
    [options, selectedRetailerLocations.length]
  )

  const messages = useDomainMessages({
    allStores: 'accountManagementDomain.form.addStore.allStores',
  })

  const valuesToShow = hasAllRetailerLocations
    ? [{ label: messages.allStores, type: OptionType.StoreGroup, value: 'allStores' }]
    : [...selectedStoreGroups, ...selectedRetailerLocations]

  return (
    <ValuesContainer>
      {valuesToShow.map(({ label, value, type }) => (
        <ValuePill
          key={`${type}-${value}`}
          label={label}
          type={type}
          value={value}
          onRemove={onRemove}
        />
      ))}
    </ValuesContainer>
  )
}
